/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/label-has-for */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import {
  get,
  groupBy,
  isArray,
  isEmpty,
  isEqual,
  map,
  find,
  pick,
  join
} from 'lodash';
import classnames from 'classnames';

import { formatFilters } from '../../../utils/benchmark';
import Filter from '../../../components/Filters/Filter';
import './Modal.css';

const getDefaultName = benchmarks => {
  const unNamed = benchmarks.filter(
    b => b.name && b.name.includes('Custom Benchmark')
  );
  if (unNamed.length) {
    return `Custom Benchmark (${unNamed.length})`;
  }

  return 'Custom Benchmark';
};

const BenchmarkModal = ({
  isOpen,
  handleClose,
  benchmarks,
  filtersConfig,
  onUpdateFilterOptions,
  handleSave,
  deleteAudienceSegment,
  handleApply,
  editingBenchmark,
  appliedBenchmark,
  contentType
}) => {
  const DEFAULT_BENCHMARK_NAME =
    contentType === 'series' ? 'Similar Shows' : 'Similar Movies';
  const [createMode, setCreateMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [active, setActive] = useState(0);
  const [segmentFilters, setSegmentFilters] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [origin, setOrigin] = useState(false);
  const [selectedBenchmark, setSelectedBenchmark] = useState(
    get(appliedBenchmark, 'uuid', null)
  );
  const [benchmarkUUID, setBenchmarkUUID] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uuidToDelete, setUuidToDelete] = useState(null);
  const [disabledSaveButton, setDisabledSaveButton] = useState(false);
  const [newBenchmarkName, setNewBenchmarkName] = useState(
    getDefaultName(benchmarks)
  );
  const [benchmarkNameError, setBenchmarkNameError] = useState(false);

  const onClickNewBenchmark = () => {
    setCreateMode(true);
    setOrigin(true);
  };
  const onChangeSearch = e => setSearchText(e.target.value.toLowerCase());

  const onChangeName = e => {
    setBenchmarkNameError(false);
    setNewBenchmarkName(e.target.value);
  };

  const onEditPress = e => {
    const uuid = e.currentTarget.value;
    e.preventDefault();
    e.stopPropagation();
    const benchmark = benchmarks.find(b => b.uuid === uuid);
    const selectedFilters = JSON.parse(benchmark.value);
    setSegmentFilters(
      segmentFilters.map(filter => {
        const af = selectedFilters.find(f => f.id === filter.id);
        if (af) {
          const { value } = af;
          return { ...filter, value };
        }
        return filter;
      })
    );
    setCreateMode(true);
    setOrigin(true);
    setNewBenchmarkName(benchmark.name);
    setBenchmarkUUID(uuid);
    setIsDefault(benchmark.isDefault);
  };

  const onClickPreview = () => {
    setPreviewMode(!previewMode);
  };

  const onconfirmDeleteSegment = e => {
    e.preventDefault();
    const uuid = e.currentTarget.value;
    setConfirmDelete(true);
    setUuidToDelete(uuid);
  };

  const deleteBenchmark = () => {
    deleteAudienceSegment(uuidToDelete);
    setUuidToDelete(null);
    setConfirmDelete(false);
  };

  const selectBenchmark = uuid => {
    if (selectedBenchmark === uuid) {
      setSelectedBenchmark(null);
    } else {
      setSelectedBenchmark(uuid);
    }
  };

  const resetFilters = () => {
    setSegmentFilters(formatFilters(filtersConfig, contentType));
  };

  useEffect(() => {
    resetFilters();
  }, [filtersConfig]);

  useEffect(() => {
    if (editingBenchmark) {
      setCreateMode(true);
      setSearchText(get(editingBenchmark, 'name', ''));
      setNewBenchmarkName(get(editingBenchmark, 'name', ''));
      setBenchmarkUUID(get(editingBenchmark, 'uuid', null));
      setIsDefault(get(editingBenchmark, 'isDefault', null));
      setSegmentFilters(
        segmentFilters.map(filter => {
          const af = JSON.parse(editingBenchmark.value || '[]').find(
            f => f.id === filter.id
          );
          if (af) {
            const { value } = af;
            return { ...filter, value };
          }
          return filter;
        })
      );
    }
  }, [editingBenchmark]);

  const countGroupSelected = g =>
    g.reduce((total, filter) => {
      if (filter.value && isArray(filter.value)) {
        return total + filter.value.length;
      }

      return filter.value ? total + 1 : total;
    }, 0);

  const handleChangeActiveFilter = filterIndex => () => {
    setActive(filterIndex);
  };

  const handleChange = (filterId, value) => {
    let filts = map(segmentFilters, filter =>
      filter.id === filterId ? { ...filter, value } : filter
    );

    if (contentType === 'series' && filterId === 'show') {
      filts = filts.map(f => ({
        ...f,
        isDisabled: value && f.id !== 'show' && f.section === 'Content'
      }));
    }

    if (contentType === 'movie' && filterId === 'movie') {
      filts = filts.map(f => ({
        ...f,
        isDisabled: value && f.id !== 'movie' && f.section === 'Content'
      }));
    }

    const filt = find(segmentFilters, f => f.id === filterId);
    if (
      contentType === 'series' &&
      filterId !== 'show' &&
      filt.section === 'Content'
    ) {
      let hasContentValues = value && value.length > 0;
      filts.forEach(f => {
        if (f.section === 'Content' && f.value && f.value > 0) {
          hasContentValues = true;
        }
      });
      filts = filts.map(f => ({
        ...f,
        isDisabled: f.id === 'show' && hasContentValues
      }));
    }

    if (
      contentType === 'movie' &&
      filterId !== 'movie' &&
      filt.section === 'Content'
    ) {
      let hasContentValues = value && value.length > 0;
      filts.forEach(f => {
        if (f.section === 'Content' && f.value && f.value > 0) {
          hasContentValues = true;
        }
      });
      filts = filts.map(f => ({
        ...f,
        isDisabled: f.id === 'movie' && hasContentValues
      }));
    }

    setSegmentFilters(filts);
  };

  const closeModal = () => {
    setSearchText('');
    setCreateMode(false);
    resetFilters();
    setSelectedBenchmark(null);
    setNewBenchmarkName(getDefaultName(benchmarks));
    setBenchmarkUUID(null);
    handleClose();
  };

  const goBackOrExit = () => {
    if (!origin) {
      handleClose();
    }
    setSearchText('');
    setCreateMode(false);
    resetFilters();
    setSelectedBenchmark(null);
    setNewBenchmarkName(getDefaultName(benchmarks));
    setBenchmarkUUID(null);
  };

  const onClickDefault = () => setIsDefault(!isDefault);

  const onClickSave = async () => {
    const sameNameBenchmarks = benchmarks.filter(
      b => b.name === newBenchmarkName
    );
    if (
      sameNameBenchmarks.length > 1 ||
      (sameNameBenchmarks.length === 1 &&
        get(sameNameBenchmarks, [0, 'uuid'], 'p') !== benchmarkUUID)
    ) {
      setBenchmarkNameError(true);
      return;
    }

    let uuid = benchmarkUUID;
    const isUsersBenchmark = find(
      benchmarks,
      b => b.uuid === get(editingBenchmark, 'uuid', null)
    );
    if (editingBenchmark && !isUsersBenchmark) {
      uuid = null;
    }

    await handleSave(uuid, newBenchmarkName, segmentFilters, isDefault);
    setNewBenchmarkName(getDefaultName(benchmarks));
    setCreateMode(false);
    setBenchmarkUUID(null);
    resetFilters();
  };

  const formatOptions = filter => {
    if (filter.id === 'date') {
      return [{ label: 'All Time', value: null }, ...filter.options];
    }

    return filter.options;
  };

  const filterGroups = groupBy(segmentFilters, f => f.section);
  const filterKeys = Object.keys(filterGroups);
  const filterItems = get(filterGroups, [filterKeys[active]], []);

  const applyBenchmark = () => {
    const bm = benchmarks.find(b => b.uuid === selectedBenchmark);
    if (bm) {
      return handleApply(JSON.parse(bm.value), bm.name, selectedBenchmark);
    }

    return handleApply(
      [],
      DEFAULT_BENCHMARK_NAME,
      `similar-${contentType === 'series' ? 'shows' : 'movies'}`
    );
  };

  const extractFilterValue = value =>
    isArray(value) ? join(map(value, 'label'), ', ') : value.label;

  const clearSearchText = () => setSearchText('');

  const isValidBenchmark = bm => {
    let invalid = false;
    JSON.parse(bm.filters).forEach(v => {
      const sf = segmentFilters.filter(f => f.id === v.id);
      if (sf.length < 1) {
        invalid = true;
      }
    });

    return invalid;
  };

  useEffect(() => {
    let activeFilters = [];

    if (Array.isArray(segmentFilters)) {
      activeFilters = segmentFilters
        .filter(({ value }) => !isEmpty(value))
        .map(f =>
          pick(f, [
            'value',
            'id',
            'isAsync',
            'isMulti',
            'label',
            'title',
            'value'
          ])
        );
    }

    if (benchmarkUUID) {
      const userBenchmark = find(benchmarks, b => b.uuid === benchmarkUUID);
      if (!userBenchmark) {
        setDisabledSaveButton(false);
      } else {
        setDisabledSaveButton(
          isEqual(activeFilters, JSON.parse(userBenchmark.value)) &&
            newBenchmarkName === userBenchmark.name &&
            userBenchmark.isDefault === isDefault
        );
      }
    } else {
      setDisabledSaveButton(
        isEmpty(activeFilters) || isEmpty(newBenchmarkName)
      );
    }
  }, [newBenchmarkName, segmentFilters, benchmarkUUID, isDefault]);

  return (
    <Modal
      className="tvt-audience-segment"
      id="tvt-benchmark-modal"
      show={isOpen}
      onHide={closeModal}
      backdrop
    >
      <div className="tvt-benchmark__modal-wrapper">
        {createMode ? (
          <div className="tvt-benchmark__new-benchmark">
            <div className="tvt-benchmark__new-benchmark-header">
              <div className="tvt-benchmark__new-benchmark-header-title">
                {benchmarkUUID ? 'Edit Benchmark' : 'New Benchmark'}
              </div>
              <input
                onChange={onChangeName}
                value={newBenchmarkName}
                id="tvt-benchmark__new-benchmark-header-input"
                className={
                  benchmarkNameError ? 'tvt-benchmark-error-input' : ''
                }
              />
              {benchmarkNameError && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="benchmark-modal__item-name-error-tooltip">
                      Benchmark Segment name already in use.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon
                    className="benchmark-name-error-icon"
                    size="1x"
                    icon="info-circle"
                  />
                </OverlayTrigger>
              )}
            </div>
            <div className="tvt-benchmark__create-new-filter-wrapper">
              <div className="tvt-benchmark__create-benchmark-sidebar">
                {Object.keys(filterGroups).map((filter, index) => {
                  const groupValueCount = countGroupSelected(
                    filterGroups[filter]
                  );
                  const buttonClassname = classnames(
                    'tvt-benchmark__create-benchmark-sidebar-button',
                    {
                      active: index === active,
                      'has-filters-selected': groupValueCount > 0
                    }
                  );
                  return (
                    <button
                      key={`audience-segment-button-${filter}`}
                      className={buttonClassname}
                      onClick={handleChangeActiveFilter(index)}
                    >
                      {filter}
                      {<span>{groupValueCount > 0 && groupValueCount}</span>}
                    </button>
                  );
                })}
              </div>
              <div className="tvt-benchmark__main">
                <>
                  {filterItems.map(filter => {
                    return (
                      <div
                        key={filter.title}
                        className={`tvt-audience-segment__filter-selection-wrapper ${
                          filter.isDisabled ? 'benchmark-filter-disabled' : ''
                        }`}
                      >
                        <div className="tvt-audience-segment__filter-selection">
                          <div className="tvt-audience-segment__title">
                            {filter.title}
                          </div>
                          <Filter
                            key={filter.id}
                            hasChanged={filter.hasChanged}
                            onChange={handleChange}
                            showInlineSelections
                            onUpdateFilterOptions={onUpdateFilterOptions}
                            {...filter}
                            options={formatOptions(filter)}
                            label={filter.benchmarkLabel || filter.label}
                            hasBoolean={false}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
                <div className="tvt-benchmark__filter-blurb">
                  {active === 0
                    ? `If a ${
                        contentType === 'series' ? 'Show' : 'Movie'
                      } filter is selected, other Content filter options are not available. If other Content filter options are selected, the ${
                        contentType === 'series' ? 'Show' : 'Movie'
                      } filter is not available.`
                    : null}
                  {active === 1
                    ? // ? 'The Panel filter can only be changed if the Country selected is United States, France, and Italy. For all other Country selections, the Panel filter defaults to total.'
                      'The Panel filter can only be changed if the Country selected is United States. For all other Country selections, the Panel filter defaults to total.'
                    : null}
                </div>
              </div>
            </div>
            <div className="tvt-benchmark__new-benchmark-footer">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="benchmark-bar__action-item-tooltip">
                    When selected, benchmark segment will be set automatically
                    when navigating through title pages.
                  </Tooltip>
                }
              >
                <label className="tvt-benchmark__default-checkbox">
                  Default Benchmark
                  <input
                    id="tvt-benchmark__default-checkbox-input"
                    type="checkbox"
                    defaultChecked={isDefault}
                    onChange={onClickDefault}
                  />
                  <span className="checkmark" />
                </label>
              </OverlayTrigger>

              <button
                onClick={disabledSaveButton ? () => null : onClickSave}
                disabled={disabledSaveButton}
                className="tvt-benchmark__new-button-save"
              >
                SAVE
              </button>
              <button
                onClick={goBackOrExit}
                className="tvt-benchmark__button-cancel"
              >
                CANCEL
              </button>
            </div>
          </div>
        ) : (
          <div className="tvt-benchmarks__benchmarks-list-wrapper">
            <div className="tvt-benchmark__list-benchmarks-header">
              <div className="tvt-benchmark__list-benchmarks-header-text">
                Benchmark Segments
              </div>
              <div className="tvt-benchmarks__benchmarks-header-search">
                <FontAwesomeIcon
                  className="search-icon benchmark-search-icon"
                  icon="search"
                  size="1x"
                />
                <input
                  onChange={onChangeSearch}
                  value={searchText}
                  placeholder="Search Benchmarks"
                />
                {searchText && searchText.length > 0 && (
                  <button
                    onClick={clearSearchText}
                    className="tvt-benchmarks__benchmarks-header-search-clear"
                  >
                    <FontAwesomeIcon
                      className="benchmark-clear-search-icon"
                      icon="times-circle"
                      size="1x"
                    />
                  </button>
                )}
              </div>
              <button
                className="tvt-benchmarks__benchmarks-header-create-new"
                onClick={onClickNewBenchmark}
              >
                Create new
              </button>
            </div>
            <div className="tvt-benchmarks__benchmarks-table-header">
              <div className="tvt-benchmarks__benchmarks-table-segment-name">
                Segment Name
              </div>
              <div className="tvt-benchmarks__benchmarks-table-segment-created">
                Created
              </div>
              <div className="tvt-benchmarks__benchmarks-table-segment-name">
                Modified
              </div>
            </div>
            <div className="tvt-benchmark__benchmark-list">
              {DEFAULT_BENCHMARK_NAME.toLowerCase().includes(searchText) ? (
                <div
                  key="default-benchmark-list-item"
                  onClick={() => selectBenchmark(null)}
                  value={null}
                  role="button"
                  onKeyPress={selectBenchmark}
                  tabIndex="0"
                  className="tvt-benchmarks__benchmarks-list-item odd"
                >
                  <label
                    htmlFor="benchmark"
                    className="tvt-benchmarks__benchmarks-list-item-radio-wrapper"
                  >
                    <input
                      type="radio"
                      checked={selectedBenchmark === null}
                      className="tvt-benchmarks__benchmarks-list-item-radio"
                    />
                    <span className="radio-button__control" />
                  </label>
                  <div className="tvt-benchmarks__benchmarks-list-item-name">
                    {DEFAULT_BENCHMARK_NAME}
                  </div>
                  <div className="tvt-benchmarks__benchmarks-list-item-created" />
                  <div className="tvt-benchmarks__benchmarks-list-item-modified" />
                </div>
              ) : null}
              {benchmarks
                .filter(s => s.name.toLowerCase().includes(searchText))
                .map((s, index) => {
                  const invalidBm = isValidBenchmark(s);
                  return (
                    <>
                      <div
                        key={s.uuid}
                        onClick={() =>
                          invalidBm ? false : selectBenchmark(s.uuid)
                        }
                        value={s.uuid}
                        role="button"
                        onKeyPress={selectBenchmark}
                        tabIndex="0"
                        className={`tvt-benchmarks__benchmarks-list-item ${
                          index % 2 === 0 ? 'even' : 'odd'
                        } ${invalidBm ? 'disabled' : ' '}`}
                      >
                        <label
                          htmlFor="benchmark"
                          className="tvt-benchmarks__benchmarks-list-item-radio-wrapper"
                        >
                          <input
                            type="radio"
                            checked={s.uuid === selectedBenchmark}
                            className="tvt-benchmarks__benchmarks-list-item-radio"
                          />
                          <span className="radio-button__control" />
                        </label>

                        <div className="tvt-benchmarks__benchmarks-list-item-name">
                          {s.name}
                        </div>
                        <div className="tvt-benchmarks__benchmarks-list-item-created">
                          {moment(s.dateCreated).format('MMM DD, YYYY')}
                        </div>
                        <div className="tvt-benchmarks__benchmarks-list-item-modified">
                          {moment(s.dateModified).format('MMM DD, YYYY')}
                        </div>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="benchmark-bar__action-item-tooltip">
                              Edit
                            </Tooltip>
                          }
                        >
                          <button
                            value={s.uuid}
                            onClick={invalidBm ? () => {} : onEditPress}
                            className="tvt-benchmarks__benchmarks-list-item-edit"
                          >
                            <FontAwesomeIcon icon="pencil-alt" size="1x" />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="benchmark-bar__action-item-tooltip">
                              Delete
                            </Tooltip>
                          }
                        >
                          <button
                            value={s.uuid}
                            onClick={onconfirmDeleteSegment}
                            className="tvt-benchmarks__benchmarks-list-item-delete"
                          >
                            <FontAwesomeIcon icon="trash-alt" size="1x" />
                          </button>
                        </OverlayTrigger>
                      </div>

                      {previewMode && selectedBenchmark === s.uuid ? (
                        <div className="tvt-benchmark__preview-row">
                          {JSON.parse(s.value).map(f => (
                            <div
                              key={f.title}
                              className="tvt-benchmark__preview-value-pill"
                            >
                              {`${f.title}: ${extractFilterValue(f.value)}`}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  );
                })}
            </div>
            <div className="tvt-benchmark__modal-footer">
              <button
                disabled={!selectedBenchmark}
                className={`tvt-benchmark__button-preview ${
                  previewMode ? 'preview-active' : ''
                }`}
                onClick={onClickPreview}
              >
                Preview
              </button>
              <button
                disabled={
                  (!selectedBenchmark &&
                    get(appliedBenchmark, 'name', DEFAULT_BENCHMARK_NAME) ===
                      DEFAULT_BENCHMARK_NAME) ||
                  get(appliedBenchmark, 'uuid', null) === selectedBenchmark
                }
                className="tvt-benchmark__button-apply"
                onClick={applyBenchmark}
              >
                Apply
              </button>
              <button
                className="tvt-benchmark__button-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <Modal
        className="tvt-audience-segment__confirm-delete-modal"
        show={confirmDelete}
      >
        <Modal.Header>
          {`Are you sure you want to delete the segment ${get(
            find(benchmarks, b => b.uuid === uuidToDelete),
            'name',
            ''
          )}?`}
        </Modal.Header>
        <Modal.Body>
          <Button
            className="tvt-audience-segment__button tvt-audience-segment__confirm-delete-button tvt-benchmark__confirm-delete-button"
            onClick={deleteBenchmark}
            variant="danger"
          >
            Delete
          </Button>
          <Button
            className="tvt-audience-segment__button tvt-audience-segment__list-cancel-button"
            onClick={() => setConfirmDelete(false)}
          >
            Cancel
          </Button>
        </Modal.Body>
      </Modal>
    </Modal>
  );
};

export default BenchmarkModal;

BenchmarkModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  benchmarks: PropTypes.arrayOf(PropTypes.shape({})),
  filtersConfig: PropTypes.shape({}).isRequired,
  onUpdateFilterOptions: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  deleteAudienceSegment: PropTypes.func.isRequired,
  handleApply: PropTypes.func.isRequired,
  appliedBenchmark: PropTypes.shape({}),
  editingBenchmark: PropTypes.string,
  contentType: PropTypes.string
};

BenchmarkModal.defaultProps = {
  benchmarks: [],
  editingBenchmark: null,
  appliedBenchmark: null,
  contentType: 'series'
};
